
import React, { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { freelancers } from "@/data/freelancers";
import { TeamBuilderHeader } from "@/components/team-builder/TeamBuilderHeader";
import { TeamBuilderTabs } from "@/components/team-builder/TeamBuilderTabs";
import { InfoCards } from "@/components/team-builder/InfoCards";

const TeamBuilder = () => {
  const { toast } = useToast();
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [projectType, setProjectType] = useState<string>("all");
  const [teamSize, setTeamSize] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedFreelancers, setSelectedFreelancers] = useState<any[]>([]);
  const [teamName, setTeamName] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("build");

  // Calculate available freelancers based on filters
  const availableFreelancers = freelancers.filter(freelancer => {
    // Filter by search query
    if (searchQuery && !freelancer.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !freelancer.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Filter by selected skills
    if (selectedSkills.length > 0 && !selectedSkills.some(skill => 
      freelancer.skills.some(fs => fs.toLowerCase().includes(skill.toLowerCase()))
    )) {
      return false;
    }
    
    // Exclude already selected freelancers
    return !selectedFreelancers.some(sf => sf.id === freelancer.id);
  });

  // Add freelancer to team
  const addToTeam = (freelancer: any) => {
    if (selectedFreelancers.length >= 5) {
      toast({
        title: "Team Size Limit",
        description: "You can add up to 5 members to your team.",
        variant: "destructive"
      });
      return;
    }
    
    setSelectedFreelancers([...selectedFreelancers, freelancer]);
    toast({
      title: "Team Member Added",
      description: `${freelancer.name} has been added to your team.`,
      variant: "success"
    });
  };

  // Remove freelancer from team
  const removeFromTeam = (freelancerId: string | number) => {
    setSelectedFreelancers(selectedFreelancers.filter(f => f.id !== freelancerId));
    toast({
      title: "Team Member Removed",
      description: "Member has been removed from your team.",
      variant: "default"
    });
  };

  // Save team
  const saveTeam = () => {
    if (!teamName.trim()) {
      toast({
        title: "Team Name Required",
        description: "Please enter a name for your team.",
        variant: "destructive"
      });
      return;
    }
    
    if (selectedFreelancers.length === 0) {
      toast({
        title: "Empty Team",
        description: "Please add at least one member to your team.",
        variant: "destructive"
      });
      return;
    }
    
    // In a real application, you would save this to your backend
    toast({
      title: "Team Saved",
      description: `Team "${teamName}" has been saved successfully.`,
      variant: "success"
    });
    
    // Reset the form
    setTeamName("");
    setSelectedFreelancers([]);
    setActiveTab("saved");
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <TeamBuilderHeader />

        <TeamBuilderTabs 
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          teamName={teamName}
          setTeamName={setTeamName}
          selectedSkills={selectedSkills}
          setSelectedSkills={setSelectedSkills}
          projectType={projectType}
          setProjectType={setProjectType}
          teamSize={teamSize}
          setTeamSize={setTeamSize}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedFreelancers={selectedFreelancers}
          availableFreelancers={availableFreelancers}
          addToTeam={addToTeam}
          removeFromTeam={removeFromTeam}
          saveTeam={saveTeam}
        />

        <InfoCards />
      </div>
    </div>
  );
};

export default TeamBuilder;
